// IndexedDB for offline metadata: downloaded book records, audio chunks
// (fallback storage when OPFS is unavailable) and local playback positions.
const DB_NAME = 'byopdf-offline';
const DB_VERSION = 1;

let dbPromise = null;

export function openDb() {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('IndexedDB unavailable'));
      return;
    }
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains('books')) {
        db.createObjectStore('books', { keyPath: 'key' });
      }
      if (!db.objectStoreNames.contains('chunks')) {
        const chunks = db.createObjectStore('chunks', { keyPath: 'id' });
        chunks.createIndex('path', 'path', { unique: false });
      }
      if (!db.objectStoreNames.contains('positions')) {
        db.createObjectStore('positions', { keyPath: 'bookId' });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error || new Error('IndexedDB open failed'));
    request.onblocked = () => reject(new Error('IndexedDB open blocked'));
  });
  dbPromise.catch(() => { dbPromise = null; });
  return dbPromise;
}

const wrap = (request) => new Promise((resolve, reject) => {
  request.onsuccess = () => resolve(request.result);
  request.onerror = () => reject(request.error || new Error('IndexedDB request failed'));
});

export async function dbGet(store, key) {
  const db = await openDb();
  return wrap(db.transaction(store, 'readonly').objectStore(store).get(key));
}

export async function dbGetAll(store) {
  const db = await openDb();
  return wrap(db.transaction(store, 'readonly').objectStore(store).getAll());
}

export async function dbPut(store, value) {
  const db = await openDb();
  return wrap(db.transaction(store, 'readwrite').objectStore(store).put(value));
}

export async function dbDelete(store, key) {
  const db = await openDb();
  return wrap(db.transaction(store, 'readwrite').objectStore(store).delete(key));
}

export async function dbGetAllByIndex(store, indexName, value) {
  const db = await openDb();
  const index = db.transaction(store, 'readonly').objectStore(store).index(indexName);
  return wrap(index.getAll(value));
}

export async function dbDeleteByIndex(store, indexName, value) {
  const db = await openDb();
  const tx = db.transaction(store, 'readwrite');
  const index = tx.objectStore(store).index(indexName);
  const keys = await wrap(index.getAllKeys(value));
  await Promise.all(keys.map((key) => wrap(tx.objectStore(store).delete(key))));
  return keys.length;
}
